import CSS from 'csstype';
import i18next from 'i18next';
import React from 'react';

import { usePanel } from '~/src/dash/components/layout/contexts';
import {
  disableSelectTextStyle,
  getAdjustedFontSize,
  getAdjustedFontSizeV2,
  getFontColor,
  getFontPresetStyle,
  getFontWeight,
} from '~/src/dash/components/text/common';
import { isOversizedLanguageWithTranslation } from '~/src/locales/common';
import { ITProps, TKey, usePublicaTranslation } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

export interface ITextProps {
  // presets
  // these will override size and weight props
  header?: boolean;
  subheader?: boolean;
  title?: boolean;
  // size
  mini?: boolean;
  extraSmall?: boolean;
  small?: boolean;
  baseSize?: boolean;
  large?: boolean;
  extraLarge?: boolean;
  sizeV2?: boolean;
  // color
  color?: string;
  muted?: boolean;
  baseColor?: boolean;
  highlighted?: boolean;
  // weight
  baseWeight?: boolean;
  bold?: boolean;
  heavy?: boolean;
  // font style
  italic?: boolean;
  // layout
  inlineBlock?: boolean;
  // misc
  disableSelect?: boolean;
  // standard
  style?: CSS.Properties;
  className?: string;
  children?: any;
  cypressID?: string;
  // localization
  tKey?: TKey; // if used, will override children
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tProps?: ITProps;
  inheritColor?: boolean;
  inheritWeight?: boolean;
  singleLineLabel?: boolean;
  addRequiredAsterisk?: boolean;
}

export const Text = ({
  // presets
  header,
  subheader,
  title,
  // size
  mini,
  extraSmall,
  small,
  baseSize,
  large,
  extraLarge,
  sizeV2, // rem
  // color
  color,
  muted,
  baseColor,
  highlighted,
  // weight
  baseWeight,
  bold,
  heavy,
  // font style
  italic,
  // layout
  inlineBlock,
  // misc
  disableSelect,
  // standard
  style,
  className = '',
  children,
  cypressID,
  // localization
  tKey = '',
  tProps = {},
  inheritColor = false,
  inheritWeight = false,
  singleLineLabel = false,
  addRequiredAsterisk = false,
}: ITextProps) => {
  const { fontColors } = usePanel();
  const { t } = usePublicaTranslation();
  const defaultColor =
    color ||
    getFontColor({
      fontColors,
      muted,
      baseColor,
      highlighted,
    });
  const fontSize = getAdjustedFontSize({
    mini,
    extraSmall,
    small,
    baseSize,
    large,
    extraLarge,
    isLanguageOversized: isOversizedLanguageWithTranslation(tKey, i18next.language),
  });
  const lineHeight = singleLineLabel ? 1 : fontSize * 1.618;
  const presetStyle = getFontPresetStyle({
    header,
    subheader,
    title,
  });
  const unselectableTextStyle = disableSelect ? disableSelectTextStyle : {};
  const fontSizeV2 = getAdjustedFontSizeV2({
    mini,
    extraSmall,
    small,
    baseSize,
    large,
    extraLarge,
    isLanguageOversized: isOversizedLanguageWithTranslation(tKey, i18next.language),
  });

  return (
    <p
      className={className}
      data-cy={cypressID}
      style={{
        display: inlineBlock ? 'inline-block' : 'block',
        verticalAlign: 'middle',
        margin: '0px',
        fontSize: sizeV2 ? `${fontSizeV2}rem` : `${fontSize}px`,
        lineHeight: singleLineLabel ? 1 : `${lineHeight}px`,
        color: inheritColor ? 'inherit' : defaultColor,
        fontWeight: inheritWeight
          ? 'inherit'
          : getFontWeight({
              baseWeight,
              bold,
              heavy,
            }),
        fontStyle: italic ? 'italic' : 'inherit',
        whiteSpace: singleLineLabel ? 'nowrap' : 'normal',
        ...unselectableTextStyle,
        ...presetStyle,
        ...style,
      }}
    >
      {tKey ? t(tKey, tProps) : children}
      {addRequiredAsterisk && (
        <span style={{ color: publicaColors.red, marginLeft: '2px' }}>*</span>
      )}
    </p>
  );
};
